import React, { useState } from 'react';
import Calculator from '../components/Calculator';
import Input from '../components/Input';
import { DollarSign, Percent, Calendar, Calculator as CalcIcon, Table, FileText, CreditCard, TrendingDown } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Helmet } from 'react-helmet-async';
import SEOHead from '../components/SEOHead';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const CarLoanAmortizationCalculator = () => {
  // State for inputs
  const [loanAmount, setLoanAmount] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [loanTerm, setLoanTerm] = useState('');
  const [downPayment, setDownPayment] = useState('0');
  const [extraPayment, setExtraPayment] = useState('0');
  const [showFullSchedule, setShowFullSchedule] = useState(false);

  // State for calculation result
  const [result, setResult] = useState<{
    monthlyPayment: number;
    totalInterest: number;
    totalCost: number;
    amortizationSchedule: Array<{
      paymentNumber: number;
      paymentDate: string;
      beginningBalance: number;
      payment: number;
      principal: number;
      interest: number;
      endingBalance: number;
      totalInterest: number;
    }>;
  } | null>(null);

  // Function to calculate loan amortization
  const calculateAmortization = () => {
    // Convert inputs to numbers
    const principal = parseFloat(loanAmount) - parseFloat(downPayment || '0');
    const rate = parseFloat(interestRate) / 100 / 12; // Monthly interest rate
    const term = parseFloat(loanTerm) * 12; // Term in months
    const extra = parseFloat(extraPayment || '0');

    if (principal > 0 && rate > 0 && term > 0) {
      // Calculate monthly payment using formula: P × (r × (1 + r)^n) / ((1 + r)^n - 1)
      const monthlyPayment = principal * (rate * Math.pow(1 + rate, term)) / (Math.pow(1 + rate, term) - 1);
      
      // Calculate amortization schedule
      let balance = principal;
      let totalInterest = 0;
      const amortizationSchedule = [];
      const today = new Date();

      for (let month = 1; month <= term; month++) {
        const interestPayment = balance * rate;
        let principalPayment = monthlyPayment - interestPayment + extra;
        
        // Make sure we don't pay more than the remaining balance
        if (principalPayment > balance) {
          principalPayment = balance;
        }
        
        balance = Math.max(0, balance - principalPayment);
        totalInterest += interestPayment;
        
        // Calculate payment date
        const paymentDate = new Date(today);
        paymentDate.setMonth(today.getMonth() + month);

        amortizationSchedule.push({
          paymentNumber: month,
          paymentDate: paymentDate.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }),
          beginningBalance: balance + principalPayment,
          payment: principalPayment + interestPayment,
          principal: principalPayment,
          interest: interestPayment,
          endingBalance: balance,
          totalInterest: totalInterest
        });

        // If balance is zero, we've paid off the loan early
        if (balance === 0) break;
      }

      setResult({
        monthlyPayment: monthlyPayment + extra,
        totalInterest: totalInterest,
        totalCost: principal + totalInterest,
        amortizationSchedule
      });
    }
  };

  // Get data for principal vs interest pie chart
  const getPieChartData = () => {
    if (!result) return [];
    
    return [
      { name: 'Principal', value: parseFloat(loanAmount) - parseFloat(downPayment || '0') },
      { name: 'Interest', value: result.totalInterest }
    ];
  };

  // Get data for amortization line chart
  const getLineChartData = () => {
    if (!result) return [];
    
    // Get data points at regular intervals
    const schedule = result.amortizationSchedule;
    const maxPoints = 12; // Show one point per year
    const interval = Math.max(1, Math.ceil(schedule.length / maxPoints));
    
    return schedule
      .filter((_, index) => index % interval === 0 || index === schedule.length - 1)
      .map(entry => ({
        month: entry.paymentNumber,
        principal: parseFloat(loanAmount) - parseFloat(downPayment || '0') - entry.totalInterest,
        interest: entry.totalInterest
      }));
  };

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(value);
  };

  return (
    <>
      <SEOHead
        title="Car Loan Amortization Schedule Calculator Australia | Payment Breakdown"
        description="Calculate your car loan amortization schedule, see monthly payment breakdowns of principal and interest, and visualize how your loan balance decreases over time."
        keywords="car loan amortization schedule calculator, auto loan payment schedule, car loan principal interest breakdown, payment schedule calculator australia, loan amortization table, car finance payment schedule"
      />
      <Helmet>
        <title>Car Loan Amortization Schedule Calculator Australia | Payment Breakdown</title>
        <meta name="description" content="Calculate your car loan amortization schedule, see monthly payment breakdowns of principal and interest, and visualize how your loan balance decreases over time." />
        <meta name="keywords" content="car loan amortization schedule calculator, auto loan payment schedule, car loan principal interest breakdown, payment schedule calculator australia, loan amortization table, car finance payment schedule" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "FinancialProduct",
              "name": "Car Loan Amortization Schedule Calculator",
              "description": "Calculate your car loan payment schedule with detailed breakdowns of principal and interest payments over the entire loan term.",
              "category": "LoanOrCredit",
              "feesAndCommissionsSpecification": "Free online calculator",
              "areaServed": {
                "@type": "Country",
                "name": "Australia"
              },
              "interactivityType": "active",
              "financialProductCategory": "LoanOrCredit"
            }
          `}
        </script>
      </Helmet>
      
      <div className="space-y-10">
        {/* Introduction Section */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Car Loan Amortization Schedule Calculator</h1>
          <div className="text-gray-600">
            <p className="mb-4">
              Our car loan amortization calculator breaks down exactly how your payments are applied to principal and interest 
              over the life of your loan. Understand how much of your car loan you've paid off at any point and track your 
              road to financial freedom with a detailed payment schedule.
            </p>
            <div className="bg-blue-50 p-4 rounded-md mb-4">
              <h2 className="text-lg font-semibold text-blue-800 mb-2">What This Calculator Shows You:</h2>
              <ul className="list-disc pl-5 space-y-1 text-blue-700">
                <li>Month-by-month breakdown of each payment</li>
                <li>How much goes toward principal vs. interest each month</li>
                <li>Your remaining loan balance after each payment</li>
                <li>Visual charts showing your loan payoff progress</li>
                <li>Impact of extra payments on your loan term and total interest</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Amortization Calculator */}
        <Calculator
          title="Car Loan Amortization Schedule Calculator"
          description="Calculate your complete car loan payment schedule with a detailed breakdown of principal and interest."
          icon={Table}
          result={result ? `Monthly Payment: ${formatCurrency(result.monthlyPayment)}` : undefined}
        >
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <Input
                label="Loan Amount"
                value={loanAmount}
                onChange={setLoanAmount}
                unit="$"
                placeholder="e.g. 30000"
              />
              <Input
                label="Down Payment"
                value={downPayment}
                onChange={setDownPayment}
                unit="$"
                placeholder="e.g. 5000"
              />
              <Input
                label="Interest Rate"
                value={interestRate}
                onChange={setInterestRate}
                unit="%"
                placeholder="e.g. 5.49"
              />
              <Input
                label="Loan Term"
                value={loanTerm}
                onChange={setLoanTerm}
                unit="years"
                placeholder="e.g. 5"
              />
              <Input
                label="Extra Monthly Payment (Optional)"
                value={extraPayment}
                onChange={setExtraPayment}
                unit="$"
                placeholder="e.g. 100"
              />
            </div>

            <button
              onClick={calculateAmortization}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition"
            >
              Calculate Amortization Schedule
            </button>

            {result !== null && (
              <div className="mt-8 space-y-8">
                {/* Summary Section */}
                <div className="p-6 bg-blue-50 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">Loan Summary</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white p-4 rounded-md shadow-sm">
                      <p className="text-sm text-gray-500">Monthly Payment</p>
                      <p className="text-xl font-bold text-blue-600">{formatCurrency(result.monthlyPayment)}</p>
                    </div>
                    <div className="bg-white p-4 rounded-md shadow-sm">
                      <p className="text-sm text-gray-500">Total Interest</p>
                      <p className="text-xl font-bold text-blue-600">{formatCurrency(result.totalInterest)}</p>
                    </div>
                    <div className="bg-white p-4 rounded-md shadow-sm">
                      <p className="text-sm text-gray-500">Total Cost</p>
                      <p className="text-xl font-bold text-blue-600">{formatCurrency(result.totalCost)}</p>
                    </div>
                  </div>
                </div>

                {/* Charts Section */}
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Principal vs Interest Pie Chart */}
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Principal vs Interest</h3>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={getPieChartData()}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {getPieChartData().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip 
                            formatter={(value) => formatCurrency(Number(value))} 
                          />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Balance Over Time Line Chart */}
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Balance Over Time</h3>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={getLineChartData()}
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="month" 
                            label={{ value: 'Month', position: 'insideBottomRight', offset: -5 }} 
                          />
                          <YAxis 
                            tickFormatter={(value) => `$${value.toLocaleString()}`}
                          />
                          <Tooltip 
                            formatter={(value) => formatCurrency(Number(value))} 
                          />
                          <Legend />
                          <Line 
                            type="monotone" 
                            dataKey="principal" 
                            name="Principal Paid" 
                            stroke="#00C49F" 
                            strokeWidth={2} 
                            activeDot={{ r: 8 }} 
                          />
                          <Line 
                            type="monotone" 
                            dataKey="interest" 
                            name="Interest Paid" 
                            stroke="#FF8042" 
                            strokeWidth={2} 
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* Amortization Schedule Table */}
                <div className="mt-8">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-semibold text-gray-800">Amortization Schedule</h3>
                    <button
                      onClick={() => setShowFullSchedule(!showFullSchedule)}
                      className="text-blue-600 hover:text-blue-800 flex items-center"
                    >
                      {showFullSchedule ? 'Show Less' : 'Show Full Schedule'} <FileText className="ml-1 h-4 w-4" />
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border border-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="py-2 px-4 border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            #
                          </th>
                          <th className="py-2 px-4 border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="py-2 px-4 border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Payment
                          </th>
                          <th className="py-2 px-4 border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Principal
                          </th>
                          <th className="py-2 px-4 border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Interest
                          </th>
                          <th className="py-2 px-4 border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Remaining Balance
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {(showFullSchedule 
                          ? result.amortizationSchedule 
                          : result.amortizationSchedule.slice(0, 12)
                        ).map((entry, index) => (
                          <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                            <td className="py-2 px-4 text-sm text-gray-500">{entry.paymentNumber}</td>
                            <td className="py-2 px-4 text-sm text-gray-500">{entry.paymentDate}</td>
                            <td className="py-2 px-4 text-sm text-gray-500">{formatCurrency(entry.payment)}</td>
                            <td className="py-2 px-4 text-sm text-gray-500">{formatCurrency(entry.principal)}</td>
                            <td className="py-2 px-4 text-sm text-gray-500">{formatCurrency(entry.interest)}</td>
                            <td className="py-2 px-4 text-sm text-gray-500">{formatCurrency(entry.endingBalance)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {!showFullSchedule && result.amortizationSchedule.length > 12 && (
                    <div className="mt-2 text-center">
                      <button
                        onClick={() => setShowFullSchedule(true)}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Show all {result.amortizationSchedule.length} payments
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </Calculator>

        {/* Information Section */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Understanding Car Loan Amortization</h2>
          <div className="space-y-4 text-gray-600">
            <p>
              Amortization is the process of spreading out a loan into a series of fixed payments over time. Each payment 
              is split between paying interest and reducing the loan principal.
            </p>
            
            <h3 className="text-lg font-semibold text-gray-700 mt-4">How Car Loan Amortization Works</h3>
            <p>
              At the beginning of your loan, a larger portion of each payment goes toward interest rather than principal. As you 
              continue making payments, this ratio shifts, with more of each payment going toward the principal and less toward interest.
            </p>
            
            <h3 className="text-lg font-semibold text-gray-700 mt-4">Why Understanding Your Amortization Schedule Matters</h3>
            <ul className="list-disc ml-6 space-y-2 mt-2">
              <li><strong>Financial planning:</strong> Know exactly how much you'll pay in total over the life of your loan</li>
              <li><strong>Early payoff strategies:</strong> See how extra payments can shorten your loan term and save on interest</li>
              <li><strong>Equity tracking:</strong> Understand when you'll reach positive equity in your vehicle</li>
              <li><strong>Refinancing decisions:</strong> Determine the best time to refinance based on your remaining balance</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-gray-700 mt-4">How Extra Payments Impact Your Loan</h3>
            <p>
              Making extra payments toward your principal can significantly impact your loan:
            </p>
            <ul className="list-disc ml-6 space-y-2 mt-2">
              <li>Reduce the total interest paid over the life of the loan</li>
              <li>Shorten the term of your loan (pay it off faster)</li>
              <li>Build equity in your vehicle more quickly</li>
            </ul>
            <p className="mt-2">
              Use our calculator's "Extra Monthly Payment" feature to see how additional payments could help you save on interest and 
              become debt-free sooner.
            </p>
            
            <div className="bg-blue-50 p-4 rounded-lg mt-6">
              <h3 className="text-lg font-semibold text-blue-800 mb-2">Pro Tip: Check For Prepayment Penalties</h3>
              <p className="text-blue-700">
                Before making extra payments, check your loan agreement for any prepayment penalties. Most Australian car loans allow 
                extra repayments without penalty, but it's always good to confirm this with your lender.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CarLoanAmortizationCalculator; 